import Head from 'next/head';
import { Fragment, ReactElement, useMemo, useRef, useState } from 'react';
import cx from 'classnames';

import { Page } from 'components/layouts';
import { Opportunity } from 'components/opportunity-page';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { OpportunityAttributes, TouchpointState } from 'lib/models/opportunity';
import { OpportunityProvider } from 'lib/providers/OpportunityProvider';
import { TouchpointType } from 'lib/models/touchpoint';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { CompanyResponse } from 'lib/models/company';
import { EventTouchpointable } from 'lib/models/event';
import { getJobPostingSchemaScript, getEventPostingSchemaScript, getEventBreadcrumbSchema, getJobFAQPageSchema } from 'utils/seo';

import styles from './OpportunityPage.module.scss';

export { styles };

export type OpportunityProps = { opportunity: OpportunityAttributes; canonicalURL?: string };

const OpportunityPage: React.FC<OpportunityProps> = ({ opportunity, canonicalURL }): ReactElement => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const [fullyScrolled, setFullyScrolled] = useState(false);
  const [notScrolled, setNotScrolled] = useState(true);
  const { title, touchpointable_type, state, meta_tags, creatable_for, public_url, touchpointable: touchpointableResponse } = opportunity;
  const isJobOrInternship = touchpointable_type === TouchpointType.Job || touchpointable_type === TouchpointType.Internship;
  const isEvent = touchpointable_type === TouchpointType.Event;
  const isPublished = state === TouchpointState.PUBLISHED;
  const eventIconURL = isEvent ? parseResponse(touchpointableResponse as EventTouchpointable).icon_url : null;
  const creatableForName = useMemo(() => parseResponse(creatable_for as CompanyResponse)?.name, [creatable_for]);

  const keywords = useMemo(() => {
    const tags = parseArrayResponse(meta_tags);
    if (tags.length === 0) return null;
    return tags.map((tag) => tag.value).join(', ');
  }, [meta_tags]);

  const metaDescription = useMemo(() => {
    // Removing the translation as it doesn't work in meta description
    return isEvent
      ? `${creatableForName} is hosting ${title}. Join on Huzzle, the top careers network for students.`
      : `${creatableForName} is looking to hire for ${title}. Apply on Huzzle, where students find their dream jobs & internships.`;
  }, [isEvent, title, creatableForName]);

  useVisibleOnIntersect({
    elRef: bottomRef,
    threshold: 0,
    onIntersect: () => setFullyScrolled(true),
    onHidden: () => setFullyScrolled(false),
    rootMargin: '0px',
  });

  useVisibleOnIntersect({
    elRef: topRef,
    threshold: 0,
    onIntersect: () => setNotScrolled(true),
    onHidden: () => setNotScrolled(false),
    rootMargin: '0px',
  });

  const pageTitle = `${title} | ${creatableForName}`;
  const faqPageSchema = useMemo(() => getJobFAQPageSchema({ opportunity }), [opportunity]);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        {keywords && <meta name="keywords" content={keywords} />}
        {isEvent && (
          <Fragment>
            <meta property="og:description" content={metaDescription}></meta>
            <meta property="og:title" content={pageTitle}></meta>
            <meta property="og:site_name" content="Huzzle"></meta>
            {eventIconURL && <meta property="og:image" content={eventIconURL}></meta>}
            <meta property="og:url" content={public_url}></meta>
          </Fragment>
        )}
        {canonicalURL ? <link rel="canonical" href={canonicalURL} /> : null}
        {isJobOrInternship && isPublished ? (
          <script
            id="jobPostingSchema"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: getJobPostingSchemaScript({ opportunity }),
            }}
          />
        ) : null}
        {isJobOrInternship && isPublished && faqPageSchema ? (
          <script
            id="jobFAQPageSchema"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: faqPageSchema,
            }}
          />
        ) : null}
        {isEvent && isPublished ? (
          <>
            <script
              id="eventPostingSchema"
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: getEventPostingSchemaScript({ opportunity }),
              }}
            />
            <script
              id="eventBreadcrumbSchema"
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: getEventBreadcrumbSchema({ opportunity }),
              }}
            />
          </>
        ) : null}
      </Head>
      <Page className={cx(styles.page, { [styles['initial-position']]: notScrolled })}>
        <div ref={topRef} className={styles['top-element']}></div>
        <OpportunityProvider opportunity={opportunity} isOpportunityPublicPage isPageFullyScrolled={fullyScrolled}>
          <Opportunity />
        </OpportunityProvider>
        <div ref={bottomRef}></div>
      </Page>
    </>
  );
};

export { OpportunityPage };
